// Floor overview (L1): every build by station and state. Blocked builds come
// first, with how long they've been blocked; filters by station, assembly and need-by.
import { useMemo, useState } from 'react';

import type { BuildSummaryResponse } from '../../api/generated/BuildSummaryResponse';
import { useBuilds, useStations } from '../../api/queries';
import { canBuild, useSession } from '../../auth/session';
import { EmptyState, ErrorPanel, Loading, PageHeader, TextField } from '../../design/components';
import { BuildCard } from '../queue/BuildCard';
import { SyncLine } from './SyncLine';

const COLUMNS = [
    { state: 'OnHold', title: 'Blocked' },
    { state: 'InProgress', title: 'In progress' },
    { state: 'Ready', title: 'Ready' },
    { state: 'Built', title: 'Built today' },
] as const;

function startOfToday(): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString();
}

export function sortBlockedFirst(builds: readonly BuildSummaryResponse[]): BuildSummaryResponse[] {
    return [...builds].sort((left, right) => (left.blockedSince ?? '9999').localeCompare(right.blockedSince ?? '9999'));
}

export function OverviewPage() {
    const session = useSession();
    const stations = useStations();
    const [station, setStation] = useState('');
    const [assembly, setAssembly] = useState('');
    const [needBy, setNeedBy] = useState('');
    const builtSince = useMemo(() => startOfToday(), []);
    const filter = {
        ...(station ? { stations: [station] } : {}),
        ...(assembly.trim() ? { assembly: assembly.trim() } : {}),
        ...(needBy ? { needByBefore: new Date(`${needBy}T23:59:59`).toISOString() } : {}),
        builtSince,
    };
    const builds = useBuilds(filter, 30_000);
    return (
        <section className="sf-stack" aria-labelledby="overview-heading">
            <PageHeader
                id="overview-heading"
                title="Floor overview"
                actions={<SyncLine canAct={canBuild(session)} />}
            />
            <div className="sf-row" role="search">
                <div className="sf-field">
                    <label htmlFor="overview-station">Station</label>
                    <select
                        id="overview-station"
                        className="sf-select"
                        value={station}
                        onChange={(event) => {
                            setStation(event.target.value);
                        }}
                    >
                        <option value="">Every station</option>
                        {stations.data?.map((option) => (
                            <option key={option.workCenter} value={option.workCenter}>
                                {option.workCenter}
                            </option>
                        ))}
                    </select>
                </div>
                <TextField label="Assembly" value={assembly} onChange={setAssembly} placeholder="Part number or name" />
                <TextField label="Needed by" type="date" value={needBy} onChange={setNeedBy} />
            </div>
            {builds.isPending ? <Loading what="the floor" /> : null}
            {builds.isError ? <ErrorPanel error={builds.error} /> : null}
            {builds.data?.length === 0 ? <EmptyState>No builds match.</EmptyState> : null}
            {builds.data
                ? COLUMNS.map((column) => {
                      const inColumn = sortBlockedFirst(builds.data.filter((build) => build.state === column.state));
                      return inColumn.length === 0 ? null : (
                          <section key={column.state} aria-labelledby={`column-${column.state}`} className="sf-stack">
                              <h2 id={`column-${column.state}`}>
                                  {column.title} ({inColumn.length})
                              </h2>
                              <ul className="sf-grid" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                  {inColumn.map((build) => (
                                      <li key={build.buildId}>
                                          <BuildCard build={build} to={`/lead/builds/${build.buildId}`} />
                                      </li>
                                  ))}
                              </ul>
                          </section>
                      );
                  })
                : null}
        </section>
    );
}
